/**
 * ============================================================================
 * METRICS API TYPES — request/response shapes for the reporting APIs
 * (HILTBusinessReportingServiceModel, model mainline)
 * ============================================================================
 *
 * The metric contract is split across TWO operations, joined by `metricKey`:
 *
 *   getMetricDefinitions → the dashboard's metric catalog: name, description,
 *        group, sortOrder, owner, goal, type, operand identity. Constant
 *        across DCRs and weeks, so it is fetched ONCE per session. Every
 *        metric appears here even when no DCR carries a value for it.
 *   getMetrics → per-record VALUES only (metricKey + value + operand values).
 *
 * Presentation therefore never comes from getMetrics: look the metricKey up in
 * the catalog (see aggregationCore.buildMetricCatalog).
 */

// ──── Shared enums (string unions — the wire sends these exact tokens) ────

export type DataMode = 'LIVE' | 'SNAPSHOT';
/**
 * The business review a request addresses on a dashboard; required on every
 * read operation.
 */
export type ReviewType = 'WBR' | 'MBR' | 'QBR' | 'YBR';
export type MetricType = 'PERCENTAGE' | 'VOLUME' | 'COUNT';
/** Goal semantics: GTE = higher is better, LTE = lower is better. */
export type GoalDirection = 'GTE' | 'LTE';
/**
 * Where a period sits relative to the current one. PAST carries settled
 * actuals, CURRENT is still accumulating, FUTURE carries forecast/commitment.
 */
export type PeriodTimeline = 'PAST' | 'CURRENT' | 'FUTURE';

/**
 * Exact numeric value serialized as a string to preserve decimal precision
 * ("82.5000"). Always parseFloat before arithmetic.
 */
export type DecimalValue = string;

// ──── getMetrics ────

export interface GetMetricsInput {
  /** Dashboard identity — a closed enum server-side ("fai"). */
  dashboardId: string;
  /** Business review addressed on the dashboard. Required by the service. */
  reviewType: ReviewType;
  /**
   * Reporting periods as Canonical Week Tokens ("2026 Week 32", zero-padded
   * NN). Built with buildDurationWindow (src/features/api/durationRange.ts)
   * from the getWeekNumbers universe — the backend owns week arithmetic,
   * including year boundaries.
   *
   * Model doc: absent defaults to the current reporting week. The UI always
   * sends explicit periods (weeks are cached individually), so it never
   * depends on that default.
   */
  reportingPeriods?: string[];
  dataMode: DataMode;
  paginationKey?: string;
  /** Restricts the response to these DCRs; absent → every DCR per period. */
  dcrIds?: string[];
}

/**
 * One metric's values for one DCR and period. Values only — join `metricKey`
 * to the catalog for name/goal/type/operand identity.
 */
export interface MetricEntry {
  metricKey: string;
  /** Absent when the metric is configured but has no value for this record. */
  value?: DecimalValue;
  /** Resolved numerator, when the metric defines one (percentage metrics). */
  numeratorValue?: DecimalValue;
  /** Resolved denominator, when the metric defines one. */
  denominatorValue?: DecimalValue;
}

export interface ChannelInfoEntry {
  channel: string;
  subChannel: string;
  /** Mapping provenance: "SELECTED" | "PREFERRED". */
  type: string[];
}

export interface DcrRecord {
  dcrId: string;
  pageLink?: string;
  title?: string;
  status?: string;
  orderCategory: string;
  deliveryType?: string;
  /**
   * Same-Week Delivery as the backend's OTD formulas resolve it (service spec
   * otd-same-week-delivery R6): `true` = only same-week deliveries count,
   * `false` = any-week. Absent for One-time DCRs (the flag is never read) and
   * on snapshots captured before the backend exposed it.
   */
  sameWeekDelivery?: boolean;
  needByWeek?: string;
  committedWeek?: string;
  completionWeek?: string;
  customerId?: string;
  customer?: string;
  localeId?: string;
  locale?: string;
  demandCategory?: string;
  demandCategorySummary?: string;
  networkSummary?: string;
  generalExpertFlag?: string;
  domain?: string;
  collectionOwner?: string;
  /** QuickSight "Customer Type" (default state shows AGIDS only). */
  customerType: 'INTERNAL' | 'AGIDS';
  isBackupDcr: boolean;
  isByocDcr: boolean;
  isOtsDcr: boolean;
  isBackfillDcr: boolean;
  isByotOfflineDcr: boolean;
  /** ISO-8601 calendar dates ("2026-01-16"). */
  dcrCreatedDate?: string;
  needByDate?: string;
  committedDeliveryDate?: string;
  /**
   * Committed date of the latest completed capacity plan explicitly labeled
   * Customer-Driven. Absent when no such plan exists — the collection is then
   * excluded from the "(New Data)" OTD metrics (absence is the exclusion
   * signal; the backend never sends a placeholder).
   */
  latestCustomerAlignedCommittedDate?: string;
  dcrCompletionDate?: string;
  channelInfo?: ChannelInfoEntry[];
  deliveryDestination?: string;
  unitOfMeasure?: string;
  intermediateServiceOrderCount?: number;
  customerDeliveryServiceOrderCount?: number;
  metrics: MetricEntry[];
}

/** One reporting period of DCR data. */
export interface DcrSnapshotData {
  /** Canonical Week Token, e.g. "2026 Week 32". */
  reportingPeriod: string;
  timeline: PeriodTimeline;
  /** Whether the period belongs in the recent-period deep-dive window. */
  showInDeepDive: boolean;
  dcrData: DcrRecord[];
}

export interface GetMetricsOutput {
  data: DcrSnapshotData[];
  /** Echoed from the request. */
  dashboardId: string;
  reviewType: ReviewType;
  dataMode: DataMode;
  /** UTC ISO-8601 instant this cache response was last published. */
  lastRefreshedAt: string;
  paginationKey?: string;
}

// ──── getMetricDefinitions (the metric catalog) ────

export interface GetMetricDefinitionsInput {
  dashboardId: string;
  reviewType: ReviewType;
}

/** Identity and semantic type of one operand; its values live per record. */
export interface MetricOperandDefinition {
  metricKey: string;
  name: string;
  type: MetricType;
}

/** Presentation metadata of one metric: everything constant across records. */
export interface MetricDefinition {
  metricKey: string;
  name: string;
  description: string;
  metricGroup: string;
  /** Dashboard row order (1-14, 17-37 in the current catalog). */
  sortOrder: number;
  owner?: string;
  /** Goal in the same semantic type as the value ("95.0000"). */
  goal?: DecimalValue;
  type: MetricType;
  /**
   * Operand identity for percentage metrics. The VALUES arrive per record as
   * MetricEntry.numeratorValue / denominatorValue, which is what makes exact
   * SUM(numerator)/SUM(denominator) aggregation possible.
   */
  numerator?: MetricOperandDefinition;
  denominator?: MetricOperandDefinition;
  /** Comparison metric for volume attainment, when applicable. */
  baseMetricKey?: string;
  goalDirection?: GoalDirection;
  /**
   * How per-DCR values aggregate into a weekly number. Absent → SUM (the
   * default for volume/count, and the SUM/SUM rate for percentage). AVERAGE =
   * mean across DCRs; PERCENTILE = the selected percentile (PERCENTILE_DISC)
   * across DCRs — e.g. Launch TAT (Avg) / (TP). Metrics with a non-SUM
   * aggregation are computed by dedicated selectors, never the generic SUM
   * paths (tables/tiles).
   */
  aggregationType?: 'SUM' | 'AVERAGE' | 'PERCENTILE';
  /**
   * Named dashboard presets that include this metric ("WBR_Metrics_Only").
   * A metric keeps its native metricGroup AND lists its preset memberships
   * here; absent when the metric belongs to no preset.
   */
  presets?: string[];
  /**
   * Display unit for the metric's values and goal ("%", "days", "items") —
   * backend spec hilt-wbr-api-serving 6.2.1. Absent → type-derived fallback
   * (PERCENTAGE implies "%", others unitless): see metricUnit() in
   * aggregationCore, the ONE resolution both tables and cards use.
   */
  unit?: string;
  /**
   * How the metric is reported per order type ("Recurring: Weekly | One-time:
   * …"). Glossary presentation only; absent for metrics with none. `description`
   * is the authoritative definition text (glossary Definition column + tooltip).
   */
  reportingBasis?: string;
  /** A worked example of the metric calculation. Glossary presentation only. */
  example?: string;
}

/** Wire value of the WBR-only preset in MetricDefinition.presets. */
export const WBR_METRICS_ONLY_PRESET = 'WBR_Metrics_Only';

/** One non-metric glossary entry (filter, tab, DCR status, domain term). */
export interface GlossaryTermDefinition {
  category: string;
  term: string;
  description: string;
}

export interface GetMetricDefinitionsOutput {
  /** The dashboard's metric catalog, ordered by sortOrder. */
  definitions: MetricDefinition[];
  /**
   * The dashboard's non-metric glossary entries. Absent when the dashboard
   * has none (an empty glossary is legal, unlike an empty catalog).
   */
  glossaryTerms?: GlossaryTermDefinition[];
  /** Echoed from the request. */
  dashboardId: string;
  reviewType: ReviewType;
  lastRefreshedAt: string;
}

export interface RefreshStatusResponse {
  lastRefreshedAt: string;
  refreshSource?: string;
}
